import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./lessonSection.module.scss";
import PlusIcon from "../../../../../assets/icons/plus_circle.svg?react";
import CloseIcon from "../../../../../assets/icons/close.svg?react";
import IconPDF from "../../../../../assets/icons/content_type_pdf.svg?react";
import IconAudio from "../../../../../assets/icons/content_type_audio.svg?react";
import IconSlide from "../../../../../assets/icons/content_type_slide.svg?react";
import IconVideo from "../../../../../assets/icons/content_type_video.svg?react";
import IconForm from "../../../../../assets/icons/content_type_feedback.svg?react";
import SlideIcon from "@assets/icons/content_type_slide_com.svg?react";
import { Content, ContentType, Lesson } from "../../../../../types";
import SectionContents from "./section-contents/SectionContents";
import LocalPopup from "@components/local-popup/LocalPopup";
import { useEditUnit } from "../../edit/useEditUnit";
import {
  useCreateContentMutation,
  ContentFragmentDoc,
  LessonType,
} from "@api/graphql-codegen";
import Spinner from "@components/spinner/Spinner";
import { logger } from '../../../../../utils/logger';

type Props = {
  contents: Content[];
  lesson: Lesson;
  lessonType: LessonType;
  lessonOpened: boolean;
  materialContents?: Content[];
};

function LessonSection(props: Props) {
  const {
    contents = [],
    lesson,
    lessonType,
    lessonOpened,
    materialContents = [],
  } = props;

  const { setContent, setLesson, setSelectionLevel } = useEditUnit();
  const [opened, setOpened] = useState(true);
  const [createPopupOpened, setCreatePopupOpened] = useState(false);
  const [matModulePopupOpened, setMatModulePopupOpened] = useState(false);

  const [createNewContent, { loading }] = useCreateContentMutation({
    onCompleted(data) {
      if (data.createContent) {
        setContent(data.createContent);
        if (matModulePopupOpened) {
          setLesson({
            ...lesson,
            materials: [
              ...(lesson?.materials || []),
              { ...data.createContent },
            ],
          });
        } else if (createPopupOpened) {
          setLesson({
            ...lesson,
            modules: [...(lesson?.modules || []), { ...data.createContent }],
          });
        }
        setSelectionLevel("Content");
        setCreatePopupOpened(false);
        setMatModulePopupOpened(false);
      }
    },
    update(cache, res) {
      const data = res.data?.createContent;
      if (data) {
        const newContentRef = cache.writeFragment({
          data,
          fragment: ContentFragmentDoc,
        });
        if (createPopupOpened) {
          cache.modify({
            id: `Lesson:${lesson?.id}`,
            fields: {
              modules(existingRefs = []) {
                return [...existingRefs, newContentRef];
              },
            },
          });
        } else if (matModulePopupOpened) {
          cache.modify({
            id: `Lesson:${lesson?.id}`,
            fields: {
              materials(existingRefs = []) {
                return [...existingRefs, newContentRef];
              },
            },
          });
        }
        handleCloseCreatePopup();
        handleCloseMatPopup();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const createContent = useCallback(
    (contentType: ContentType) => () => {
      const nextPosition =
        contents.reduce((acc: any, c) => {
          if (c.position! > acc) return c.position;
          return acc;
        }, 0) + 1;
      createNewContent({
        variables: {
          input: {
            lessonId: lesson.id,
            type: contentType,
            lessonType: lessonType,
            position: nextPosition,
          },
        },
      });
      // handleCloseCreatePopup();
    },
    [contents, createNewContent, lesson.id, lessonType]
  );

  const createMaterialContent = useCallback(
    (contentType: ContentType) => () => {
      const nextPosition =
        materialContents.reduce((acc: any, c) => {
          if (c.position! > acc) return c.position;
          return acc;
        }, 0) + 1;
      createNewContent({
        variables: {
          input: {
            lessonId: lesson.id,
            type: contentType,
            lessonType: LessonType.LESSONMATERIAL,
            position: nextPosition,
          },
        },
      });
      // handleCloseMatPopup();
    },
    [materialContents, createNewContent, lesson.id]
  );

  useEffect(() => {
    if (lessonOpened) setOpened(true);
  }, [lessonOpened]);

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(" ");
  }, [opened]);

  const handleShowCreatePopup = () => {
    setCreatePopupOpened(true);
  };

  const handleCloseCreatePopup = () => {
    setCreatePopupOpened(false);
  };

  const handleShowMatPopup = () => {
    setMatModulePopupOpened(true);
  };

  const handleCloseMatPopup = () => {
    setMatModulePopupOpened(false);
  };

  const popupContent = useMemo(() => {
    return (
      <div className={styles.popup}>
        <div className={styles.popupHeader}>
          <div className={styles.popupTitle}>Add content</div>
          <div
            className={styles.popupCloseWrapper}
            onClick={handleCloseCreatePopup}
          >
            <CloseIcon />
          </div>
        </div>
        {loading ? (
          <div className={styles.popupLoading}>
            <Spinner width={20} height={20} />
          </div>
        ) : (
          <div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.VIDEO)}
              >
                <IconVideo />
                <div className={styles.itemName}>Video</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.AUDIO)}
              >
                <IconAudio />
                <div className={styles.itemName}>Audio</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.SLIDE)}
              >
                <IconSlide />
                <div className={styles.itemName}>Slides</div>
              </div>
            </div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.PDF)}
              >
                <IconPDF />
                <div className={styles.itemName}>PDF</div>
              </div>
              <div
                className={styles.popupItemForm}
                onClick={createContent(ContentType.FORM)}
              >
                <IconForm />
                <div className={styles.itemName}>Form</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.PARENT)}
              >
                <div>PS</div>
                <div className={styles.itemName}>Parent</div>
              </div>
            </div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.REVEALSLIDE)}
              >
                <SlideIcon />
                <div className={styles.itemName}>Slides.com</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.ZIPSLIDE)}
              >
                <SlideIcon />
                <div className={styles.itemName}>Zip Slides</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [createContent, loading]);

  const materialPopupContent = useMemo(() => {
    return (
      <div className={styles.matPopup}>
        <div className={styles.popupHeader}>
          <div className={styles.popupTitle}>Add content</div>
          <div
            className={styles.popupCloseWrapper}
            onClick={handleCloseMatPopup}
          >
            <CloseIcon />
          </div>
        </div>
        <div>
          {loading ? (
            <div className={styles.popupLoading}>
              <Spinner width={20} height={20} />
            </div>
          ) : (
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createMaterialContent(ContentType.PDF)}
              >
                <IconPDF />
                <div className={styles.itemName}>PDF</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [createMaterialContent, loading]);

  return (
    <div className={wrapperClassName}>
      <div className={styles.content}>
        <SectionContents contents={contents} lesson={lesson} />
        <LocalPopup
          content={popupContent}
          open={createPopupOpened}
          onClose={handleCloseCreatePopup}
        >
          <div
            className={styles.addContentWrapper}
            onClick={handleShowCreatePopup}
          >
            <PlusIcon className={styles.addButtonIcon} />
            <div className={styles.addButtonTitle}>Add new module</div>
            {loading && createPopupOpened ? (
              <div className={styles.loading}>
                <Spinner width={20} height={20} />
              </div>
            ) : null}
          </div>
        </LocalPopup>
        <>
          <SectionContents
            contents={materialContents ?? []}
            lesson={lesson}
            isSupportingMaterial={true}
          />
          <LocalPopup
            content={materialPopupContent}
            open={matModulePopupOpened}
            onClose={handleCloseMatPopup}
          >
            <div
              className={styles.addContentWrapper}
              onClick={handleShowMatPopup}
            >
              <PlusIcon className={styles.addButtonIcon} />
              <div className={styles.addButtonTitle}>
                Add Supporting Material
              </div>
              {loading && matModulePopupOpened ? (
                <div className={styles.loading}>
                  <Spinner width={20} height={20} />
                </div>
              ) : null}
            </div>
          </LocalPopup>
        </>
      </div>
    </div>
  );
}

export default React.memo(LessonSection);
